// Tenant context for the admin UI (PRD §3.3 step 3, §10 P1).
//
// A signed-in user belongs to exactly one tenant in v1, recorded server-side in
// users/{uid} (written by the tenantSignup / tenantAcceptInvite callables). The
// client only READS that doc — it never writes membership (CLAUDE.md rule 2).
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { useAuth } from './useAuth';

export interface TenantState {
  /** True until the first users/{uid} snapshot resolves. */
  loading: boolean;
  /** The caller's tenant id, or null when they have not signed up yet. */
  tenantId: string | null;
}

/** Subscribe to the caller's users/{uid} doc to derive their tenant. */
export function useTenant(): TenantState {
  const { user } = useAuth();
  const [state, setState] = useState<TenantState>({ loading: true, tenantId: null });

  useEffect(() => {
    if (!user) {
      setState({ loading: false, tenantId: null });
      return;
    }
    setState({ loading: true, tenantId: null });
    const ref = doc(db, 'users', user.uid);
    return onSnapshot(
      ref,
      (snap) => {
        const tenantId = snap.exists() ? ((snap.data().tenantId as string) ?? null) : null;
        setState({ loading: false, tenantId });
      },
      () => setState({ loading: false, tenantId: null }),
    );
  }, [user]);

  return state;
}

export interface SignupResult {
  tenantId: string;
  created: boolean;
}

/** Provision a tenant for the current user (idempotent server-side). A 30s
 *  client timeout means a hung callable surfaces an error instead of spinning. */
export async function provisionTenant(businessName: string): Promise<SignupResult> {
  const call = httpsCallable<{ businessName: string }, SignupResult>(functions, 'tenantSignup', {
    timeout: 30_000,
  });
  const res = await call({ businessName });
  return res.data;
}
